<template>
  <div>
    <head>
      <meta charset="utf-8"/>
      <title>Kenya Internet Coverage Mapping | Multi-Provider Network Analysis</title>
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <meta content="Multi-Provider Internet Coverage and Connectivity Mapping for Kenya" name="description"/>
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/libs/datatables.net-bs4/css/dataTables.bootstrap4.min.css" rel="stylesheet"/>
      <link href="assets/libs/datatables.net-buttons-bs4/css/buttons.bootstrap4.min.css" rel="stylesheet"/>
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet"/>
      <link href="assets/css/icons.min.css" rel="stylesheet"/>
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet"/>
      <link href="assets/css/custom.css" rel="stylesheet"/>
      <link href="assets/css/map.css" rel="stylesheet"/>
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar/>

      <div id="content" class="content-minimize">
        <!-- Modals -->
        <InternetPointDetailsModal
            :point="selectedPoint"
            :show="showDetailsModal"
            @close="showDetailsModal = false"
            @edit="openEditModal"
            @delete="deletePoint"
        />

        <AddEditInternetPointModal
            :point="editingPoint"
            :show="showAddEditModal"
            :isEdit="isEditMode"
            @close="closeAddEditModal"
            @save="saveInternetPoint"
        />

        <InternetStatsModal
            :show="showStatsModal"
            :stats="statistics"
            @close="showStatsModal = false"
        />

        <!-- Provider Comparison Modal -->
        <ProviderComparisonModal
            :show="showProviderModal"
            :providers="providerStats"
            @close="showProviderModal = false"
        />

        <div class="page-content">
          <div class="container-fluid">
            <!-- Enhanced Page Title with Provider Info -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <div>
                    <h4 class="mb-1 font-size-18">Kenya Internet Coverage Map</h4>
                    <p class="text-muted mb-0">
                      Multi-Provider Network Analysis |
                      <span class="badge bg-success">{{ activeProviders.length }} Active Providers</span> |
                      <span class="badge bg-info">{{ totalInternetPoints }} Coverage Points</span>
                    </p>
                  </div>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="index.html">Dashboards</a></li>
                      <li class="breadcrumb-item active">Internet Mapping</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- Provider Quick Stats Bar -->
            <div class="row mb-3">
              <div class="col-12">
                <div class="card bg-primary text-white">
                  <div class="card-body py-2">
                    <div class="row text-center">
                      <div class="col-md-2" v-for="provider in topProviders" :key="provider.name">
                        <div class="d-flex align-items-center justify-content-center">
                          <div class="me-2">
                            <img :src="getProviderLogo(provider.name)" width="30" height="30" class="rounded" :alt="provider.name">
                          </div>
                          <div>
                            <h6 class="mb-0 text-white">{{ provider.name }}</h6>
                            <small class="text-white-50">{{ provider.points }} points</small>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-2">
                        <button class="btn btn-light btn-sm" @click="showProviderModal = true">
                          <i class="mdi mdi-chart-bar"></i> Compare All
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Enhanced Toolbar with Provider Filters -->
            <InternetToolBar
                @addPoint="openAddModal"
                @showStats="loadStatistics"
                @exportData="exportPoints"
                @toggleView="toggleMapView"
                :viewMode="viewMode"
                :stats="quickStats"
                @filterByProvider="filterByProvider"
                :activeProviders="activeProviders"
            />

            <!-- Main Content -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <!-- Map View -->
                    <div v-if="viewMode === 'map'" class="position-relative">
                      <div id="internet-map" class="w-100 mt-1 h-100 live-map-cont main-map-container" style="height: 600px;"></div>

                      <!-- Enhanced Map Search -->
                      <div class="form-group map-searcher">
                        <input
                            type="text"
                            class="form-control form-control-lg text-black"
                            placeholder="Search by location, provider, or connection type"
                            id="internet-pac-input"
                            v-model="searchQuery"
                        >
                        <button
                            v-if="searchQuery"
                            @click="clearSearch"
                            title="Clear search"
                            class="btn btn-transparent border-0 clear-map"
                        >
                          <i class="mdi mdi-close"></i>
                        </button>
                      </div>

                      <!-- Enhanced Map Legend -->
                      <div class="map-key-card card" @click="showLegend = !showLegend">
                        <div class="card-body">
                          <i class="mdi mdi-key-variant"></i>
                        </div>
                      </div>

                      <!-- Legend with Provider Colors -->
                      <div class="card map-card animated slideInLeft" v-show="showLegend">
                        <span class="close-map-key" @click="showLegend = false">
                          <i class="zmdi zmdi-close"></i>
                        </span>
                        <div class="card-body">
                          <h4 class="card-title">Map Legend</h4>

                          <!-- Connection Types -->
                          <h6 class="mt-3 mb-2">Connection Types</h6>
                          <div class="listview py-2 map-key">
                            <span class="listview__item py-1" v-for="type in connectionTypes" :key="type">
                              <img :src="getConnectionTypeIcon(type)" class="listview__img" alt="">
                              <div class="listview__content d-flex align-items-center">
                                <div class="listview__heading"><strong>{{ type }}</strong></div>
                              </div>
                            </span>
                          </div>

                          <!-- Provider Colors -->
                          <h6 class="mt-3 mb-2">Service Providers</h6>
                          <div class="listview py-2 map-key">
                            <span class="listview__item py-1" v-for="provider in activeProviders" :key="provider.name">
                              <div class="provider-color-box me-2" :style="`background-color: ${provider.color}`"></div>
                              <div class="listview__content d-flex align-items-center">
                                <div class="listview__heading">
                                  <strong>{{ provider.name }}</strong>
                                  <small class="text-muted d-block">{{ provider.points }} points</small>
                                </div>
                              </div>
                            </span>
                          </div>
                        </div>
                      </div>

                      <!-- Enhanced Filters -->
                      <div class="position-absolute" style="top: 10px; right: 10px; z-index: 1000;">
                        <div class="btn-group">
                          <button type="button" class="btn btn-primary dropdown-toggle" data-bs-toggle="dropdown">
                            <i class="mdi mdi-filter"></i> Advanced Filters
                          </button>
                          <div class="dropdown-menu dropdown-menu-end p-3" style="min-width: 350px;">

                            <!-- Provider Filter -->
                            <h6 class="dropdown-header">Service Providers</h6>
                            <div class="row">
                              <div class="col-6" v-for="provider in activeProviders.slice(0, 6)" :key="provider.name">
                                <div class="form-check form-check-sm">
                                  <input class="form-check-input" type="checkbox"
                                         v-model="filters.providers"
                                         :value="provider.name"
                                         :id="`filter${provider.name.replace(/\s+/g, '')}`">
                                  <label class="form-check-label small" :for="`filter${provider.name.replace(/\s+/g, '')}`">
                                    {{ provider.name }}
                                  </label>
                                </div>
                              </div>
                            </div>

                            <hr>

                            <!-- Connection Type Filter -->
                            <h6 class="dropdown-header">Connection Type</h6>
                            <div class="row">
                              <div class="col-6" v-for="type in connectionTypes" :key="type">
                                <div class="form-check form-check-sm">
                                  <input class="form-check-input" type="checkbox"
                                         v-model="filters.connectionTypes"
                                         :value="type"
                                         :id="`filterType${type}`">
                                  <label class="form-check-label small" :for="`filterType${type}`">{{ type }}</label>
                                </div>
                              </div>
                            </div>

                            <hr>

                            <!-- Speed and Cost Filters -->
                            <h6 class="dropdown-header">Speed & Cost Filters</h6>
                            <div class="row">
                              <div class="col-6">
                                <label class="small">Min Speed (Mbps)</label>
                                <input type="number" class="form-control form-control-sm" v-model="filters.minSpeed">
                              </div>
                              <div class="col-6">
                                <label class="small">Max Cost (KES)</label>
                                <input type="number" class="form-control form-control-sm" v-model="filters.maxCost">
                              </div>
                            </div>

                            <hr>

                            <!-- Reliability Filter -->
                            <div class="form-group">
                              <label class="small">Minimum Reliability (%)</label>
                              <input type="range" class="form-range" v-model="filters.minReliability" min="0" max="100">
                              <small class="text-muted">{{ filters.minReliability }}% and above</small>
                            </div>

                            <button class="btn btn-sm btn-primary mt-3 w-100" @click="applyFilters">
                              Apply Filters
                            </button>
                            <button class="btn btn-sm btn-outline-secondary mt-2 w-100" @click="resetFilters">
                              Reset All
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Enhanced Table View -->
                    <div v-else-if="viewMode === 'table'">
                      <InternetPointsTable
                          :points="internetPoints"
                          @view="viewPointDetails"
                          @edit="openEditModal"
                          @delete="deletePoint"
                          :showProviderColumn="true"
                      />
                    </div>

                    <!-- Provider Comparison View -->
                    <div v-else-if="viewMode === 'providers'">
                      <ProviderAnalysisView
                          :providers="providerStats"
                          :points="internetPoints"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6">© County - Multi-Provider Internet Coverage</div>
              <div class="col-sm-6">
                <div class="text-sm-end d-sm-block">
                  Powered by {{ activeProviders.length }} Network Providers
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>

    <div class="rightbar-overlay"></div>
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import InternetToolBar from "@/components/internet/InternetToolBar";
import InternetPointDetailsModal from "@/components/internet/InternetPointDetailsModal";
import AddEditInternetPointModal from "@/components/internet/AddEditInternetPointModal";
import InternetStatsModal from "@/components/internet/InternetStatsModal";
import InternetPointsTable from "@/components/internet/InternetPointsTable";
import ProviderComparisonModal from "@/components/internet/ProviderComparisonModal";
import ProviderAnalysisView from "@/components/internet/ProviderAnalysisView";
import { execute, biller } from "@/api";
import moment from "moment";

export default {
  name: "InternetMapping",
  components: {
    NavigationBar, InternetToolBar, InternetPointDetailsModal, AddEditInternetPointModal,
    InternetStatsModal, InternetPointsTable, ProviderComparisonModal, ProviderAnalysisView
  },
  data() {
    return {
      viewMode: 'map', // 'map', 'table', 'providers'
      internetPoints: [],
      selectedPoint: null,
      editingPoint: null,
      isEditMode: false,
      showDetailsModal: false,
      showAddEditModal: false,
      showStatsModal: false,
      showProviderModal: false,
      showLegend: false,
      statistics: {},
      providerStats: [],
      map: null,
      markers: [],
      infoWindows: [],
      searchQuery: '',
      searchBox: null,
      filters: {
        providers: [],
        connectionTypes: ['Fiber', '4G', '5G', 'Wireless', 'Satellite'],
        minSpeed: 0,
        maxCost: 50000,
        minReliability: 0
      },
      loading: false,
      county: { lat: -1.286389, lng: 36.817223 },

      // Provider-specific data
      providerColors: {
        'Safaricom': '#00AA44',
        'Zuku (Wananchi Group)': '#FF6600',
        'Airtel Kenya': '#FF0000',
        'Telkom Kenya': '#0066CC',
        'JTL Faiba': '#8B008B',
        'Poa Internet': '#00CCCC',
        'Liquid Telecom': '#FF69B4',
        'Mawingu Networks': '#32CD32'
      },

      connectionTypes: ['Fiber', '4G', '5G', 'Wireless', 'Satellite'],

      markerIcons: {
        'Fiber': 'assets/images/map-assets/internet/fiber.svg',
        '4G': 'assets/images/map-assets/internet/4g.svg',
        '5G': 'assets/images/map-assets/internet/5g.svg',
        'Wireless': 'assets/images/map-assets/internet/wireless.svg',
        'Satellite': 'assets/images/map-assets/internet/satellite.svg'
      }
    }
  },
  computed: {
    activeProviders() {
      const providers = {};
      this.internetPoints.forEach(point => {
        if (point.provider) {
          if (!providers[point.provider]) {
            providers[point.provider] = {
              name: point.provider,
              points: 0,
              color: this.providerColors[point.provider] || '#666666'
            };
          }
          providers[point.provider].points++;
        }
      });
      return Object.values(providers).sort((a, b) => b.points - a.points);
    },

    topProviders() {
      return this.activeProviders.slice(0, 5);
    },

    totalInternetPoints() {
      return this.internetPoints.length;
    },

    quickStats() {
      return {
        total: this.internetPoints.length,
        fiber: this.internetPoints.filter(p => p.connectionType === 'Fiber').length,
        fourG: this.internetPoints.filter(p => ['4G', '5G'].includes(p.connectionType)).length,
        wireless: this.internetPoints.filter(p => p.connectionType === 'Wireless').length,
        providers: this.activeProviders.length
      };
    }
  },
  mounted() {
    this.loadInternetPoints();
    this.menuAccess();

    // Initialize filters with all providers
    this.filters.providers = [];

    setTimeout(() => {
      if (this.viewMode === 'map') {
        this.initMap();
      }
    }, 500);
  },
  watch: {
    viewMode(newVal) {
      if (newVal === 'map') {
        setTimeout(() => {
          if (!this.map) {
            this.initMap();
          }
        }, 100);
      }
    }
  },
  methods: {
    menuAccess() {
      const data = new FormData();
      data.append("function", "menuAccess");
      data.append("menu", "Internet Mapping");
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      execute(data, biller).then().catch(e => console.error("Menu access error:", e));
    },

    loadInternetPoints() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getInternetPoints");
      data.append("status", "active");

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.internetPoints = res.data.data.internetPoints || [];
              console.log('Internet points loaded:', this.internetPoints);

              // Load provider statistics
              this.loadProviderStatistics();

              if (this.map) {
                this.updateMapMarkers();
              }
            } else {
              this.$swal({
                icon: 'error',
                title: 'Error',
                text: res.data.message || 'Failed to load internet points'
              });
            }
            this.loading = false;
          })
          .catch((e) => {
            this.$swal({ icon: 'error', title: 'Error', text: e.message });
            this.loading = false;
          });
    },

    loadProviderStatistics() {
      const stats = {};
      this.internetPoints.forEach(point => {
        if (!stats[point.provider]) {
          stats[point.provider] = {
            name: point.provider,
            totalPoints: 0,
            avgSpeed: 0,
            avgCost: 0,
            avgReliability: 0,
            connectionTypes: {},
            regions: {},
            totalSpeed: 0,
            totalCost: 0,
            totalReliability: 0,
            validSpeedCount: 0,
            validCostCount: 0,
            validReliabilityCount: 0
          };
        }

        const providerStat = stats[point.provider];
        providerStat.totalPoints++;

        // Connection types
        if (!providerStat.connectionTypes[point.connectionType]) {
          providerStat.connectionTypes[point.connectionType] = 0;
        }
        providerStat.connectionTypes[point.connectionType]++;

        // Regions
        if (!providerStat.regions[point.region]) {
          providerStat.regions[point.region] = 0;
        }
        providerStat.regions[point.region]++;

        // Calculate averages
        if (point.speed) {
          providerStat.totalSpeed += parseFloat(point.speed);
          providerStat.validSpeedCount++;
        }
        if (point.cost) {
          providerStat.totalCost += parseFloat(point.cost);
          providerStat.validCostCount++;
        }
        if (point.reliability) {
          providerStat.totalReliability += parseFloat(point.reliability);
          providerStat.validReliabilityCount++;
        }
      });

      // Calculate final averages
      Object.values(stats).forEach(stat => {
        stat.avgSpeed = stat.validSpeedCount > 0 ? (stat.totalSpeed / stat.validSpeedCount).toFixed(1) : 0;
        stat.avgCost = stat.validCostCount > 0 ? (stat.totalCost / stat.validCostCount).toFixed(0) : 0;
        stat.avgReliability = stat.validReliabilityCount > 0 ? (stat.totalReliability / stat.validReliabilityCount).toFixed(1) : 0;
      });

      this.providerStats = Object.values(stats).sort((a, b) => b.totalPoints - a.totalPoints);
    },

    initMap() {
      const options = {
        zoom: 12,
        center: { lat: this.county.lat, lng: this.county.lng },
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        styles: [
          { "featureType": "poi", "elementType": "labels", "stylers": [{ "visibility": "off" }] }
        ]
      };

      this.map = new google.maps.Map(document.getElementById('internet-map'), options);
      this.setupSearchBox();
      this.updateMapMarkers();
    },

    setupSearchBox() {
      const input = document.getElementById("internet-pac-input");
      this.searchBox = new google.maps.places.SearchBox(input);
      this.map.controls[google.maps.ControlPosition.TOP_LEFT].push(input);

      this.map.addListener("bounds_changed", () => {
        this.searchBox.setBounds(this.map.getBounds());
      });

      this.searchBox.addListener("places_changed", () => {
        const places = this.searchBox.getPlaces();
        if (places.length == 0) return;

        const bounds = new google.maps.LatLngBounds();
        places.forEach(place => {
          if (!place.geometry) return;
          if (place.geometry.viewport) {
            bounds.union(place.geometry.viewport);
          } else {
            bounds.extend(place.geometry.location);
          }
        });
        this.map.fitBounds(bounds);
      });
    },

    updateMapMarkers() {
      if (!this.map) return;

      this.clearMarkers();
      const filteredPoints = this.getFilteredPoints();

      filteredPoints.forEach(point => {
        this.addMarker(point);
      });
    },

    getFilteredPoints() {
      return this.internetPoints.filter(point => {
        // Provider filter
        if (this.filters.providers.length > 0 && !this.filters.providers.includes(point.provider)) {
          return false;
        }

        // Connection type filter
        if (!this.filters.connectionTypes.includes(point.connectionType)) {
          return false;
        }

        // Speed filter
        const speed = parseFloat(point.speed) || 0;
        if (speed < this.filters.minSpeed) return false;

        // Cost filter
        const cost = parseFloat(point.cost) || 0;
        if (cost > this.filters.maxCost) return false;

        // Reliability filter
        const reliability = parseFloat(point.reliability) || 0;
        if (reliability < this.filters.minReliability) return false;

        return true;
      });
    },

    addMarker(point) {
      const iconUrl = this.markerIcons[point.connectionType] || this.markerIcons['Wireless'];
      const providerColor = this.providerColors[point.provider] || '#666666';

      const marker = new google.maps.Marker({
        position: { lat: parseFloat(point.latitude), lng: parseFloat(point.longitude) },
        map: this.map,
        icon: {
          url: iconUrl,
          scaledSize: new google.maps.Size(40, 40),
          origin: new google.maps.Point(0, 0),
          anchor: new google.maps.Point(20, 40)
        },
        animation: google.maps.Animation.DROP
      });

      // Add provider-colored circle around marker
      const circle = new google.maps.Circle({
        strokeColor: providerColor,
        strokeOpacity: 0.8,
        strokeWeight: 3,
        fillColor: providerColor,
        fillOpacity: 0.2,
        map: this.map,
        center: marker.getPosition(),
        radius: 100
      });

      const infoWindow = new google.maps.InfoWindow({
        content: this.createEnhancedInfoWindowContent(point)
      });

      marker.addListener('click', () => {
        this.infoWindows.forEach(iw => iw.close());
        infoWindow.open(this.map, marker);
        this.selectedPoint = point;
      });

      marker.addListener('dblclick', () => {
        this.viewPointDetails(point);
      });

      this.markers.push(marker);
      this.infoWindows.push(infoWindow);
    },

    createEnhancedInfoWindowContent(point) {
      const providerColor = this.providerColors[point.provider] || '#666666';
      return `
        <div class="card mb-0" style="min-width: 350px;">
          <div class="card-header" style="background-color: ${providerColor}; color: white;">
            <h6 class="mb-0">
              <i class="mdi mdi-wifi me-1"></i>
              ${point.name}
            </h6>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-8">
                <p class="text-muted mb-1"><i class="mdi mdi-map-marker"></i> ${point.region}</p>
                <div class="d-flex justify-content-between mb-1">
                  <span><strong>Provider:</strong></span>
                  <span class="badge" style="background-color: ${providerColor};">${point.provider}</span>
                </div>
                <div class="d-flex justify-content-between mb-1">
                  <span><strong>Type:</strong></span>
                  <span>${point.connectionType}</span>
                </div>
                <div class="d-flex justify-content-between mb-1">
                  <span><strong>Speed:</strong></span>
                  <span class="text-success">${point.speed} Mbps</span>
                </div>
                ${point.cost ? `
                <div class="d-flex justify-content-between mb-1">
                  <span><strong>Cost:</strong></span>
                  <span>KES ${point.cost}/month</span>
                </div>
                ` : ''}
                ${point.reliability ? `
                <div class="d-flex justify-content-between mb-1">
                  <span><strong>Reliability:</strong></span>
                  <span class="text-info">${point.reliability}%</span>
                </div>
                ` : ''}
              </div>
              <div class="col-4 text-center">
                ${point.rating ? `
                <div class="mb-2">
                  <div class="text-warning">
                    ${'⭐'.repeat(parseInt(point.rating))}
                  </div>
                  <small class="text-muted">${point.rating}/5</small>
                </div>
                ` : ''}
              </div>
            </div>

            ${point.packageName ? `<p class="text-muted small mb-2"><strong>Package:</strong> ${point.packageName}</p>` : ''}
            ${point.notes ? `<p class="text-muted small mb-2">${point.notes}</p>` : ''}

            <div class="d-flex justify-content-between mt-2">
              <button class="btn btn-sm btn-primary" onclick="window.viewInternetPoint(${point.id})">
                <i class="mdi mdi-eye"></i> Details
              </button>
              <button class="btn btn-sm btn-success" onclick="window.editInternetPoint(${point.id})">
                <i class="mdi mdi-pencil"></i> Edit
              </button>
            </div>
          </div>
        </div>
      `;
    },

    clearMarkers() {
      this.markers.forEach(marker => marker.setMap(null));
      this.markers = [];
      this.infoWindows = [];
    },

    applyFilters() {
      this.updateMapMarkers();
    },

    resetFilters() {
      this.filters = {
        providers: [],
        connectionTypes: ['Fiber', '4G', '5G', 'Wireless', 'Satellite'],
        minSpeed: 0,
        maxCost: 50000,
        minReliability: 0
      };
      this.updateMapMarkers();
    },

    filterByProvider(providerName) {
      if (providerName === 'all') {
        this.filters.providers = [];
      } else {
        this.filters.providers = [providerName];
      }
      this.updateMapMarkers();
    },

    getProviderLogo(providerName) {
      const logos = {
        'Safaricom': 'assets/images/providers/safaricom-logo.png',
        'Zuku (Wananchi Group)': 'assets/images/providers/zuku-logo.png',
        'Airtel Kenya': 'assets/images/providers/airtel-logo.png',
        'Telkom Kenya': 'assets/images/providers/telkom-logo.png',
        'JTL Faiba': 'assets/images/providers/faiba-logo.png',
        'Poa Internet': 'assets/images/providers/poa-logo.png',
        'Liquid Telecom': 'assets/images/providers/liquid-logo.png',
        'Mawingu Networks': 'assets/images/providers/mawingu-logo.png'
      };
      return logos[providerName] || 'assets/images/providers/default-provider.png';
    },

    getConnectionTypeIcon(type) {
      return this.markerIcons[type] || this.markerIcons['Wireless'];
    },

    clearSearch() {
      this.searchQuery = '';
      this.map.setCenter({ lat: this.county.lat, lng: this.county.lng });
      this.map.setZoom(12);
    },

    toggleMapView() {
      const modes = ['map', 'table', 'providers'];
      const currentIndex = modes.indexOf(this.viewMode);
      this.viewMode = modes[(currentIndex + 1) % modes.length];
    },

    // Modal management methods
    openAddModal() {
      this.editingPoint = null;
      this.isEditMode = false;
      this.showAddEditModal = true;
    },

    openEditModal(point) {
      this.editingPoint = point;
      this.isEditMode = true;
      this.showAddEditModal = true;
    },

    closeAddEditModal() {
      this.showAddEditModal = false;
      this.editingPoint = null;
      this.isEditMode = false;
    },

    viewPointDetails(point) {
      this.selectedPoint = point;
      this.showDetailsModal = true;
    },

    saveInternetPoint(pointData) {
      const data = new FormData();
      data.append("function", this.isEditMode ? "updateInternetPoint" : "saveInternetPoint");

      if (this.isEditMode) {
        data.append("id", pointData.id);
        data.append("updatedBy", sessionStorage.getItem("sessionNames"));
      } else {
        data.append("addedBy", sessionStorage.getItem("sessionNames"));
      }

      // Append all form data
      Object.keys(pointData).forEach(key => {
        if (pointData[key] !== null && pointData[key] !== undefined) {
          data.append(key, pointData[key]);
        }
      });

      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.$swal({
                icon: 'success',
                title: 'Success',
                text: res.data.message
              });
              this.closeAddEditModal();
              this.loadInternetPoints();
            } else {
              this.$swal({
                icon: 'error',
                title: 'Error',
                text: res.data.message
              });
            }
          })
          .catch((e) => {
            this.$swal({
              icon: 'error',
              title: 'Error',
              text: e.message
            });
          });
    },

    deletePoint(point) {
      this.$swal({
        title: 'Are you sure?',
        text: `Delete internet point "${point.name}" (${point.provider})?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
      }).then((result) => {
        if (result.isConfirmed) {
          const data = new FormData();
          data.append("function", "deleteInternetPoint");
          data.append("id", point.id);
          data.append("deletedBy", sessionStorage.getItem("sessionNames"));
          data.append("sessionNames", sessionStorage.getItem("sessionNames"));
          data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

          execute(data, biller)
              .then((res) => {
                if (res.data.success) {
                  this.$swal({
                    icon: 'success',
                    title: 'Deleted!',
                    text: res.data.message
                  });
                  this.loadInternetPoints();
                } else {
                  this.$swal({
                    icon: 'error',
                    title: 'Error',
                    text: res.data.message
                  });
                }
              })
              .catch((e) => {
                this.$swal({
                  icon: 'error',
                  title: 'Error',
                  text: e.message
                });
              });
        }
      });
    },

    loadStatistics() {
      this.statistics = {
        totalPoints: this.internetPoints.length,
        providerStats: this.providerStats,
        connectionStats: this.connectionTypes.map(type => ({
          connectionType: type,
          count: this.internetPoints.filter(p => p.connectionType === type).length,
          avgSpeed: this.calculateAvgSpeed(type)
        })),
        regionStats: this.calculateRegionStats()
      };
      this.showStatsModal = true;
    },

    calculateAvgSpeed(connectionType) {
      const points = this.internetPoints.filter(p => p.connectionType === connectionType);
      if (points.length === 0) return 0;
      const totalSpeed = points.reduce((sum, p) => sum + (parseFloat(p.speed) || 0), 0);
      return (totalSpeed / points.length).toFixed(1);
    },

    calculateRegionStats() {
      const regions = {};
      this.internetPoints.forEach(point => {
        if (!regions[point.region]) {
          regions[point.region] = { region: point.region, count: 0 };
        }
        regions[point.region].count++;
      });
      return Object.values(regions);
    },

    exportPoints() {
      const exportData = this.internetPoints.map(point => ({
        name: point.name,
        region: point.region,
        provider: point.provider,
        connectionType: point.connectionType,
        speed: point.speed,
        cost: point.cost,
        reliability: point.reliability,
        packageName: point.packageName,
        latitude: point.latitude,
        longitude: point.longitude,
        dateAdded: point.dateAdded,
        addedBy: point.addedBy
      }));

      const csvContent = this.convertToCSV(exportData);
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kenya_internet_coverage_${moment().format('YYYY-MM-DD')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },

    convertToCSV(data) {
      if (!data || data.length === 0) return '';
      const headers = Object.keys(data[0]);
      const csvHeaders = headers.join(',');
      const csvRows = data.map(row => {
        return headers.map(header => {
          const value = row[header];
          return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
        }).join(',');
      });
      return [csvHeaders, ...csvRows].join('\n');
    }
  },

  beforeDestroy() {
    this.clearMarkers();
    if (this.map) {
      this.map = null;
    }
  },

  created() {
    // Make functions available globally for info window buttons
    window.viewInternetPoint = (id) => {
      const point = this.internetPoints.find(p => p.id === id);
      if (point) this.viewPointDetails(point);
    };

    window.editInternetPoint = (id) => {
      const point = this.internetPoints.find(p => p.id === id);
      if (point) this.openEditModal(point);
    };
  }
}
</script>

<style scoped>
.main-map-container {
  position: relative;
  min-height: 600px;
}

.map-searcher {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1000;
  width: 350px;
}

.map-searcher input {
  padding-right: 40px;
}

.map-searcher .clear-map {
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
}

.map-key-card {
  position: absolute;
  bottom: 20px;
  left: 10px;
  z-index: 1000;
  cursor: pointer;
  width: 50px;
  height: 50px;
}

.map-card {
  position: absolute;
  bottom: 80px;
  left: 10px;
  z-index: 1000;
  width: 350px;
  max-height: 500px;
  overflow-y: auto;
}

.close-map-key {
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
  font-size: 20px;
}

.listview__item {
  display: flex;
  align-items: center;
  padding: 8px;
}

.listview__img {
  width: 30px;
  height: 30px;
  margin-right: 15px;
}

.provider-color-box {
  width: 20px;
  height: 20px;
  border-radius: 3px;
  display: inline-block;
}

.form-check-sm .form-check-input {
  margin-top: 0.1rem;
}

.form-check-sm .form-check-label {
  font-size: 0.85rem;
}
</style>